import {
  SENSITIVE_ROLES,
  canEnterStage,
  canMoveActivity,
  goldForActivity,
  normalizeToBrl,
  positionBetween,
  recurrenceLabel,
  suggestProjectKey,
  validateManagerAssignment,
  type Activity,
  type Checklist,
  type ColumnFlowRole,
  type Expense,
  type Member,
  type Occurrence,
  type Project,
  type Role,
} from '@sistemas/contracts'
import * as React from 'react'

import { fetchCoreCatalog } from '@/modules/core/core-api'
import {
  acknowledgeAlertRemote,
  deleteExpense,
  deleteExpenseGroup,
  deletePersonCost,
  insertAuditEntry,
  insertExpense,
  insertExpenseInstallments,
  insertOccurrence,
  insertPersonCost,
  upsertAllocation,
  upsertBudget,
  applyBudgetDefault as applyBudgetDefaultRemote,
  fetchBudgetMonths,
} from '@/modules/core/core-writes'
import { currentMonthLocal, todayLocalIso } from '@/lib/dates'
import { removeAvatar, uploadAvatar } from '@/lib/avatars'
import { useSession } from '@/lib/session'
import { useLiveRefresh, type LiveTable } from '@/lib/realtime'
import {
  fetchPeopleCatalog,
  createMemberFromSso,
  linkMemberSso,
  patchMember,
  reactivateMemberRemote,
  replaceMemberRoles,
  softDeleteMember,
} from '@/modules/people/people-api'
import {
  archiveActivityRemote,
  deleteProjectRemote,
  fetchProjectsCatalog,
  insertActivity,
  insertActivityEvent,
  insertProject,
  insertStage,
  insertColumn,
  patchColumn,
  deleteColumnRemote,
  deleteStageRemote,
  moveProjectRemote,
  patchStage,
  insertGoldEntry,
  insertProjectMessage,
  fetchProjectMessages,
  linkRepository,
  moveActivityRemote,
  patchActivity,
  patchProject,
  reassignActivitiesRemote,
  restoreActivityFromTrashRemote,
  restoreActivityRemote,
  trashActivityRemote,
  type ActivityEventKind,
} from '@/modules/projects/projects-api'
import {
  deleteEventRemote,
  insertEvent,
  patchEvent,
  rescheduleEvent as rescheduleEventRemote,
  upsertOccurrenceSlot,
  type EventInput,
} from '@/modules/agenda/events-api'
import {
  deleteRoutineRemote,
  insertCategory,
  insertRoutine,
  patchCategory,
  patchRoutine,
  setRoutineActiveRemote,
  type CategoryInput,
  type RoutineInput,
} from '@/modules/routines/routines-api'
import type { ActivityPatch } from '@/modules/projects/activity-patch'
import { fetchTickets } from '@/modules/tickets/tickets-api'
import {
  INITIAL_STATUS,
  coreReducer,
  initialState,
  toggleChecklistItemIn,
  type ActivityEventRow,
  type AuditEntry,
  type ChatMessage,
  type CoreAction,
  type CoreState,
  type CoreStatus,
  type LoadStatus,
  type MemberRoleRow,
} from '@/modules/projects/store-reducer'

export type {
  ActivityEventRow,
  AuditEntry,
  ChatMessage,
  CoreState,
  CoreStatus,
  LoadStatus,
  MemberRoleRow,
}

/**
 * Estado do Hub.
 *
 * Pessoas (C1), projetos, atividades e chat (C2/C3) persistem no schema
 * `sistemas` do Supabase, assim como o catálogo C4–C10 (lido de uma vez por
 * `fetchCoreCatalog`).
 *
 * O estado local é **cache da UI**, não fonte: toda escrita vai ao banco
 * primeiro e só reflete aqui (via `dispatch`) se o banco aceitar. Isso é
 * deliberado — com o kanban colaborativo, um estado local que "vence" o banco
 * mostraria um card que ninguém mais enxerga.
 *
 * Nada deste estado vai para o `localStorage`. Persistir o cache inteiro
 * gravava salários, lançamentos, GOLD e auditoria em texto claro no navegador
 * e contornava o RLS para quem abrisse o mesmo perfil (D11 / regra 6 do FOCO).
 * O que sobrevive ao F5 é só preferência de UI (sidebar, tema) — e vive nos
 * seus próprios hooks.
 *
 * As funções de escrita leem `stateRef.current`, nunca o `state` do render em
 * que foram criadas. Sem isso, duas ações rápidas sobre a mesma atividade
 * partiam do mesmo snapshot velho e a segunda desfazia a primeira.
 */

/**
 * Chave do cache antigo. Apagada no boot para limpar o que já vazou nas
 * máquinas do time; remover esta linha depois de duas versões publicadas.
 */
const LEGACY_STORAGE_KEY = 'sistemas:core:v2'

/**
 * Papéis que exigem justificativa registrada, como no banco.
 * A lista mora no contrato para não divergir do trigger
 * `member_role_require_reason` — reexportada aqui por compatibilidade.
 */
export { SENSITIVE_ROLES }

/** O que a exclusão definitiva de uma pessoa remove — para a confirmação forte. */
export interface MemberDeletionImpact {
  activities: number
  openActivities: number
  costs: number
  allocations: number
  gold: number
  roles: number
  messages: number
  managedMembers: number
  ledProjects: number
}

const uid = () => crypto.randomUUID()
const nowIso = () => new Date().toISOString()

/** Dia e mês no fuso da pessoa — nunca UTC (ver lib/dates.ts). Nomes mantidos pelos chamadores. */
export const todayIso = todayLocalIso
export const currentMonth = currentMonthLocal

/**
 * Patch de projeto. `undefined` é significativo: limpa o campo opcional
 * (descrição, responsável). Por isso não é um `Partial` puro — com
 * `exactOptionalPropertyTypes`, `Partial` não aceitaria limpar nada.
 */
export type ProjectPatch = {
  -readonly [K in keyof Omit<Project, 'id' | 'createdAt'>]?: Project[K] | undefined
}

type Result = { ok: true } | { ok: false; reason: string }

/**
 * As ações do Hub. A identidade deste objeto é estável entre renders — quem
 * só dispara ações (formulários, botões) pode usar `useCoreActions()` e não
 * re-renderiza a cada mudança de estado.
 */
export interface CoreActions {
  audit: (action: string, resource: string, detail: string) => void

  // --- C1 Pessoas (Supabase) ---
  /**
   * Cria a pessoa a partir de um usuário do SSO. Não há e-mail no input: ele
   * vem do SSO, e o digitado à mão foi o que deixou gente do time sem login.
   */
  createMember: (input: {
    userId: string
    fullName?: string
    positionTitle?: string
    managerId?: string
    githubLogin?: string
    weeklyCapacityHours?: number
    startedOn?: string
  }) => Promise<Member | { error: string }>
  /** Vincula uma ficha antiga, sem login, a um usuário do SSO. */
  linkMemberToSso: (memberId: string, userId: string) => Promise<Result>
  updateMember: (
    id: string,
    patch: {
      fullName?: string
      email?: string
      positionTitle?: string | undefined
      /** `null` limpa (raiz). `undefined` no patch = não altera. */
      managerId?: string | null
      githubLogin?: string | undefined
      weeklyCapacityHours?: number | undefined
      startedOn?: string | undefined
    },
  ) => Promise<Result>
  /**
   * Troca (arquivo) ou remove (`null`) a foto de perfil. Própria pessoa ou
   * gestão; o banco recusa o resto (`set_member_avatar`).
   */
  setMemberAvatar: (memberId: string, file: File | null) => Promise<Result>
  /** Q48: soft-delete. Recusa se houver atividade aberta sem reatribuir. */
  deactivateMember: (
    id: string,
  ) => Promise<{ ok: true } | { ok: false; reason: string; openActivities: number }>
  reactivateMember: (id: string) => Promise<Result>
  /** Inventário do impacto — alimenta a confirmação de desligamento. */
  memberDeletionImpact: (id: string) => MemberDeletionImpact
  /**
   * Q48: no banco não há DELETE de membro. “Excluir” na UI desativa
   * (`is_active=false` + `terminated_on`) e preserva histórico.
   */
  deleteMember: (id: string) => Promise<Result>
  /** Reatribui em lote — usado no offboarding antes de desativar. */
  reassignActivities: (
    fromMemberId: string,
    toMemberId: string,
  ) => Promise<{ ok: true; count: number } | { ok: false; reason: string }>
  setMemberRoles: (memberId: string, roles: Role[], reason?: string) => Promise<Result>
  /**
   * Registra a remuneração numa vigência. O total plenamente carregado é
   * derivado (salário + encargos + benefícios) — nunca digitado solto, senão
   * o número do departamento não bate com a soma das pessoas.
   */
  setPersonCost: (
    memberId: string,
    input: { salary: number; charges?: number; benefits?: number },
    effectiveFrom: string,
  ) => Promise<Result>
  /** Remove uma vigência específica — corrige lançamento errado sem apagar o histórico. */
  removePersonCost: (memberId: string, effectiveFrom: string) => Promise<Result>
  setAllocation: (
    memberId: string,
    projectId: string,
    month: string,
    percent: number,
  ) => Promise<Result>

  /** Carga (ou recarga) de cada domínio a partir do Supabase. */
  /** `silent`: relê sem mostrar "carregando" (é o tempo real atualizando a tela). */
  reloadProjects: (options?: { silent?: boolean }) => Promise<void>
  reloadPeople: () => Promise<void>
  reloadCore: () => Promise<void>

  createProject: (input: {
    name: string
    key?: string
    type?: Project['type']
    color?: string
    description?: string
    visibility?: Project['visibility']
    leaderId?: string
    repositoryId?: string
  }) => Promise<Project | { error: string }>
  updateProject: (id: string, patch: ProjectPatch) => Promise<Result>
  /** Vincula (ou desvincula com `null`) o projeto a um repositório GitHub. */
  linkProjectRepository: (projectId: string, repositoryId: string | null) => Promise<Result>
  /**
   * Move o projeto para outra coluna do quadro de Projetos, entre `beforeId` e
   * `afterId` (ausentes = topo/fim). Otimista: o card fica onde foi solto e só
   * volta se o banco recusar.
   */
  moveProject: (
    projectId: string,
    toStageId: string,
    beforeId?: string,
    afterId?: string,
  ) => Promise<Result>
  /**
   * Colunas do quadro de atividades de um projeto. Quem edita o projeto mexe
   * nelas; entrada e conclusão só se renomeiam (`board_column_guard`).
   */
  createColumn: (input: { projectId: string; name: string; wipLimit: number | null }) => Promise<Result>
  updateColumn: (
    id: string,
    patch: { name?: string; wipLimit?: number | null; flowRole?: ColumnFlowRole | null },
  ) => Promise<Result>
  /** Reordena entre `beforeId` e `afterId`. Otimista, como o card. */
  moveColumn: (id: string, beforeId?: string, afterId?: string) => Promise<Result>
  /** Exclui a coluna; as atividades vão para o fim de `targetId`. */
  deleteColumn: (id: string, targetId: string | null) => Promise<Result>
  /** Colunas do quadro de Projetos — estrutura da gestão (RLS `can_manage_people`). */
  createStage: (input: { name: string; status: Project['status'] }) => Promise<Result>
  updateStage: (id: string, patch: { name?: string; status?: Project['status'] }) => Promise<Result>
  /** Reordena a coluna para entre `beforeId` e `afterId`. Otimista, como o card. */
  moveStage: (id: string, beforeId?: string, afterId?: string) => Promise<Result>
  /** Exclui a coluna; os projetos dela vão para o fim de `targetId`. */
  deleteStage: (id: string, targetId: string | null) => Promise<Result>
  /** Remove o projeto e tudo que pende dele: colunas, atividades e conversas. */
  deleteProject: (id: string) => Promise<Result>
  createActivity: (input: {
    projectId: string
    columnId: string
    title: string
    description?: string
    assigneeId: string | null
    priority: Activity['priority']
    type?: Activity['type']
    difficulty: number | null
    startOn?: string
    dueOn?: string
    labelIds: string[]
    origin?: Activity['origin']
    /** A demanda já nasce com os passos (migration 20261002120000). */
    checklists?: Checklist[]
  }) => Promise<Activity | { error: string }>
  updateActivity: (id: string, patch: ActivityPatch) => Promise<Result>
  moveActivity: (
    activityId: string,
    toColumnId: string,
    beforeId?: string,
    afterId?: string,
  ) => Promise<Result>
  /** Soft-delete (migration 012): sai do quadro, o histórico fica. Recusa chamado em aberto (R11). */
  archiveActivity: (id: string) => Promise<Result>
  restoreActivity: (id: string) => Promise<Result>
  /** Lixeira (ADR-0017): some de tudo, restaurável por 30 dias. Recusa chamado em aberto (R11). */
  trashActivity: (id: string) => Promise<Result>
  restoreActivityFromTrash: (id: string) => Promise<Result>
  addChecklist: (activityId: string, checklist: Checklist) => Promise<Result>
  addChecklistItem: (activityId: string, checklistId: string, text: string) => Promise<Result>
  toggleChecklistItem: (activityId: string, checklistId: string, itemId: string) => Promise<Result>
  removeChecklist: (activityId: string, checklistId: string) => Promise<Result>

  /** Relê as mensagens de um projeto (o chat aberto chama a cada 15 s). */
  refreshProjectMessages: (projectId: string) => Promise<void>
  sendMessage: (projectId: string, body: string, attachmentName?: string) => Promise<Result>
  /** Relê a fila ao abrir a tela (chamados são só leitura no Hub, ADR-0024). */
  refreshTickets: () => Promise<void>

  /** C5 — cria a rotina e seus participantes numa escrita só. */
  createRoutine: (input: RoutineInput) => Promise<Result>
  updateRoutine: (id: string, input: RoutineInput) => Promise<Result>
  /** Desativar: sai da agenda, o histórico fica (R3). */
  setRoutineActive: (id: string, isActive: boolean) => Promise<Result>
  /** Apaga de vez. Recusado quando a rotina já tem ocorrência registrada. */
  deleteRoutine: (id: string) => Promise<Result>

  /** C5 — taxonomia da agenda, compartilhada por rotina e evento. */
  createCategory: (input: CategoryInput) => Promise<Result>
  updateCategory: (
    id: string,
    patch: Partial<CategoryInput> & { isActive?: boolean },
  ) => Promise<Result>

  /** C5 — compromisso avulso da agenda. */
  createEvent: (input: EventInput) => Promise<Result>
  updateEvent: (id: string, input: EventInput) => Promise<Result>
  /** Remarcar mexe só em data e hora — o resto do compromisso fica igual. */
  rescheduleEvent: (id: string, date: string, time: string | null) => Promise<Result>
  deleteEvent: (id: string) => Promise<Result>

  /**
   * C5 — remarca ou cancela UMA ocorrência da série (R2).
   * `originalDate` é a vaga na recorrência; `date`, onde ela passa a acontecer.
   */
  rescheduleOccurrence: (input: {
    routineId: string
    originalDate: string
    date: string
    status: Occurrence['status']
  }) => Promise<Result>

  /** C5 — registra uma ocorrência da rotina (presença, ata, status). */
  registerOccurrence: (input: {
    routineId: string
    date: string
    originalDate: string
    status: Occurrence['status']
    minutes?: string | undefined
    agenda?: string | undefined
    attendance: Occurrence['attendance']
  }) => Promise<Result>

  /** `reason` é obrigatório na tela para mês já fechado (SDD 19) e vai para a auditoria. */
  saveBudget: (month: string, amount: number, reason?: string) => Promise<Result>
  /** Grava o padrão do ano e o aplica aos meses futuros não editados à mão. */
  applyBudgetDefault: (year: number, amount: number) => Promise<Result & { applied?: number }>
  /** Devolve o id: o comprovante sobe para o caminho do lançamento recém-criado. */
  addExpense: (
    input: Omit<Expense, 'id' | 'amountBrl' | 'createdBy'>,
  ) => Promise<{ ok: true; id: string } | { ok: false; reason: string }>
  removeExpense: (id: string) => Promise<Result>
  /**
   * SIS-63: o `originalAmount` é o TOTAL, dividido em `count` parcelas mensais.
   * Devolve os ids na ordem das parcelas; o comprovante sobe para a 1ª.
   */
  addExpenseInstallments: (
    input: Omit<Expense, 'id' | 'amountBrl' | 'createdBy' | 'installment' | 'splits' | 'recurring' | 'renewalDate'>,
    count: number,
  ) => Promise<{ ok: true; ids: string[]; group: string } | { ok: false; reason: string }>
  /** SIS-63: exclui todas as parcelas do grupo. */
  removeExpenseGroup: (group: string) => Promise<Result>

  errorToActivity: (
    errorGroupId: string,
    projectId: string,
    columnId: string,
    assigneeId: string | null,
  ) => Promise<Result>
  acknowledgeAlert: (alertId: string) => Promise<Result>

  resetAll: () => void
}

export interface CoreApi extends CoreActions {
  readonly state: CoreState
  readonly me: Member
  readonly status: CoreStatus
  /** Atalhos de compatibilidade sobre `status.projects`. */
  readonly projectsError: string | null
  readonly projectsLoading: boolean
}

interface CoreSnapshot {
  readonly state: CoreState
  readonly me: Member
  readonly status: CoreStatus
}

const CoreActionsContext = React.createContext<CoreActions | null>(null)
const CoreSnapshotContext = React.createContext<CoreSnapshot | null>(null)

const PROJECT_LIVE_TABLES: readonly LiveTable[] = [
  'project',
  'project_stage',
  'board_column',
  'activity',
  'activity_event',
  'activity_label',
]
const TICKET_LIVE_TABLES: readonly LiveTable[] = ['ticket', 'ticket_activity']

export function CoreProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(coreReducer, undefined, initialState)
  const [status, setStatus] = React.useState<CoreStatus>(INITIAL_STATUS)
  const session = useSession()
  const sessionMemberId = session.member?.id ?? null

  // Snapshot sempre atual para as funções de escrita (ver cabeçalho do arquivo).
  // `commit` aplica o reducer no ref ANTES de o React renderizar: duas escritas
  // encadeadas na mesma microtask enxergam o resultado uma da outra, sem
  // esperar o ciclo de render. O reducer é puro, então o React chega ao mesmo
  // estado — o ref só chega primeiro.
  const stateRef = React.useRef(state)
  stateRef.current = state
  const commit = React.useCallback((action: CoreAction) => {
    stateRef.current = coreReducer(stateRef.current, action)
    dispatch(action)
  }, [])

  // A identidade que assina evento e autoria é a da sessão SSO, nunca uma
  // heurística sobre a lista de membros: `activity_event.author_id` passa por
  // RLS contra `current_member_id()`, então "quem eu acho que sou" e "quem o
  // banco acha que eu sou" precisam ser a mesma pessoa.
  const me = React.useMemo<Member>(() => {
    const known = sessionMemberId
      ? state.members.find((m) => m.id === sessionMemberId)
      : undefined
    if (known) return known
    // O SessionProvider é gate: só renderiza filhos com sessão resolvida e
    // ficha ativa. Chegar aqui sem `member` é bug de composição, não estado.
    if (!session.member) {
      throw new Error('CoreProvider precisa de uma sessão autenticada (SessionProvider).')
    }
    return {
      id: session.member.id,
      fullName: session.member.displayName,
      email: session.member.email,
      initials: session.member.displayName.slice(0, 2).toUpperCase(),
      isActive: true,
    }
  }, [state.members, session.member, sessionMemberId])
  const meRef = React.useRef(me)
  meRef.current = me

  React.useEffect(() => {
    try {
      window.localStorage.removeItem(LEGACY_STORAGE_KEY)
    } catch {
      /* storage indisponível: nada a limpar */
    }
  }, [])

  const setDomainStatus = React.useCallback((domain: keyof CoreStatus, next: LoadStatus) => {
    setStatus((prev) => ({ ...prev, [domain]: next }))
  }, [])

  const reloadProjects = React.useCallback(async (options?: { silent?: boolean }) => {
    // Clique em "tentar de novo" passa o evento como argumento: só `true` é silencioso.
    const silent = options?.silent === true
    if (!silent) setDomainStatus('projects', { kind: 'carregando' })
    const result = await fetchProjectsCatalog()
    if (!result.ok) {
      if (!silent) setDomainStatus('projects', { kind: 'erro', reason: result.reason })
      return
    }
    commit({ type: 'hydrate/projects', data: result.data })
    setDomainStatus('projects', { kind: 'ok' })
  }, [setDomainStatus, commit])

  const reloadPeople = React.useCallback(async () => {
    setDomainStatus('people', { kind: 'carregando' })
    const result = await fetchPeopleCatalog()
    if (!result.ok) {
      setDomainStatus('people', { kind: 'erro', reason: result.reason })
      return
    }
    commit({ type: 'hydrate/people', data: result.data })
    setDomainStatus('people', { kind: 'ok' })
  }, [setDomainStatus, commit])

  // C4–C10: alimentadas por worker e por formulário, todas lidas do banco.
  // Erro aqui não derruba a tela de projetos, que tem carga própria — mas
  // fica registrado em `status.core` para a tela dizer "falhou", não "vazio".
  const reloadCore = React.useCallback(async () => {
    setDomainStatus('core', { kind: 'carregando' })
    const result = await fetchCoreCatalog()
    if (!result.ok) {
      setDomainStatus('core', { kind: 'erro', reason: result.reason })
      return
    }
    commit({ type: 'hydrate/core', data: result.data })
    setDomainStatus('core', { kind: 'ok' })
  }, [setDomainStatus, commit])

  React.useEffect(() => {
    void reloadProjects()
    void reloadPeople()
    void reloadCore()
  }, [reloadProjects, reloadPeople, reloadCore])

  /**
   * Escritas na mesma atividade rodam em fila. Um clique que chega enquanto o
   * anterior ainda está no banco espera por ele e parte do resultado — é o
   * que impede "marquei dois itens e um desmarcou sozinho".
   */
  const writeQueues = React.useRef(new Map<string, Promise<unknown>>())

  const actions = React.useMemo<CoreActions>(() => {
    const current = () => stateRef.current

    /**
     * Trilha de auditoria. Grava no banco — auditoria em `localStorage` não é
     * auditoria, some com um clique em limpar dados do navegador (02 §4).
     *
     * A linha só entra na lista local depois que o banco a aceitou: uma
     * trilha que mostra o que não gravou não é trilha.
     */
    function audit(action: string, resource: string, detail: string) {
      void (async () => {
        const result = await insertAuditEntry({ action, resource, detail }, meRef.current.id)
        if (!result.ok) {
          console.warn(`[auditoria] não gravou "${action}" (${resource}): ${result.reason}`)
          return
        }
        commit({ type: 'audit/push', entry: result.data })
      })()
    }

    /**
     * Grava o evento no banco e reflete na timeline local.
     *
     * O evento é o único registro de *quando* algo mudou — sem ele não existe
     * lead time, previsibilidade nem recálculo retroativo de GOLD. Por isso a
     * falha é registrada na auditoria em vez de passar batido: um event log com
     * buraco silencioso mente sobre o passado, e ninguém descobre a tempo.
     */
    async function pushEvent(
      activityId: string,
      kind: ActivityEventKind,
      detail?: { from?: string | undefined; to?: string | undefined },
    ) {
      const result = await insertActivityEvent(
        {
          activityId,
          kind,
          ...(detail?.from !== undefined ? { from: detail.from } : {}),
          ...(detail?.to !== undefined ? { to: detail.to } : {}),
        },
        meRef.current.id,
      )

      if (!result.ok) {
        audit('evento.falhou', activityId, `${kind}: ${result.reason}`)
        return
      }

      commit({ type: 'event/push', event: result.data })
    }

    function serialize<T>(activityId: string, work: () => Promise<T>): Promise<T> {
      const previous = writeQueues.current.get(activityId) ?? Promise.resolve()
      const next = previous.then(work, work)
      writeQueues.current.set(activityId, next)
      void next.finally(() => {
        if (writeQueues.current.get(activityId) === next) writeQueues.current.delete(activityId)
      })
      return next
    }

    function finalColumnIds() {
      return new Set(
        current()
          .columns.filter((c) => c.kind === 'final')
          .map((c) => c.id),
      )
    }

    async function updateActivity(id: string, patch: ActivityPatch): Promise<Result> {
      const state = current()
      const before = state.activities.find((a) => a.id === id)
      const remote = await patchActivity(id, patch)
      if (!remote.ok) return { ok: false, reason: remote.reason }

      commit({ type: 'activity/upsert', activity: remote.data })

      if ('assigneeId' in patch && (patch.assigneeId ?? null) !== (before?.assigneeId ?? null)) {
        const nameOf = (memberId: string | null | undefined) =>
          memberId
            ? (state.members.find((m) => m.id === memberId)?.fullName ?? memberId)
            : 'sem responsável'
        await pushEvent(id, 'responsavel_alterado', {
          from: nameOf(before?.assigneeId),
          to: nameOf(patch.assigneeId),
        })
      }
      if ('dueOn' in patch && (patch.dueOn ?? null) !== (before?.dueOn ?? null)) {
        await pushEvent(id, 'prazo_alterado', {
          ...(before?.dueOn ? { from: before.dueOn } : {}),
          to: patch.dueOn ?? 'removido',
        })
      }
      if (
        'difficulty' in patch &&
        patch.difficulty !== null &&
        patch.difficulty !== undefined &&
        patch.difficulty !== before?.difficulty
      ) {
        await pushEvent(id, 'dificuldade_definida', {
          ...(before?.difficulty !== null && before?.difficulty !== undefined
            ? { from: String(before.difficulty) }
            : {}),
          to: String(patch.difficulty),
        })
      }
      return { ok: true }
    }

    /** Aplica uma transformação nos checklists partindo do estado *depois* da escrita anterior. */
    function patchChecklists(
      activityId: string,
      transform: (activity: Activity) => Activity['checklists'],
    ): Promise<Result> {
      return serialize(activityId, async () => {
        const activity = current().activities.find((a) => a.id === activityId)
        if (!activity) return { ok: false, reason: 'Atividade não encontrada.' }
        return updateActivity(activityId, { checklists: transform(activity) })
      })
    }

    async function refreshTickets() {
      const remote = await fetchTickets()
      if (remote.ok) commit({ type: 'tickets/replace', tickets: remote.data })
    }

    async function createActivity(
      input: Parameters<CoreActions['createActivity']>[0],
    ): Promise<Activity | { error: string }> {
      const last = current()
        .activities.filter((a) => a.columnId === input.columnId)
        .sort((a, b) => a.position - b.position)
        .at(-1)

      // `key` não é enviada: quem gera é a sequence do banco. O evento
      // `criada` sai junto, dentro de insertActivity.
      const remote = await insertActivity(
        {
          projectId: input.projectId,
          columnId: input.columnId,
          position: positionBetween(last?.position),
          title: input.title,
          assigneeId: input.assigneeId,
          difficulty: input.difficulty,
          priority: input.priority,
          ...(input.type ? { type: input.type } : {}),
          origin: input.origin ?? 'manual',
          labelIds: input.labelIds,
          ...(input.description ? { description: input.description } : {}),
          ...(input.startOn ? { startOn: input.startOn } : {}),
          ...(input.dueOn ? { dueOn: input.dueOn } : {}),
          ...(input.checklists?.length ? { checklists: input.checklists } : {}),
        },
        meRef.current.id,
      )
      if (!remote.ok) return { error: remote.reason }

      const { activity, event } = remote.data
      commit({ type: 'activity/add', activity, event })
      return activity
    }

    async function deactivateMember(
      id: string,
    ): Promise<{ ok: true } | { ok: false; reason: string; openActivities: number }> {
      const finals = finalColumnIds()
      const open = current().activities.filter(
        (a) => a.assigneeId === id && !finals.has(a.columnId),
      )
      if (open.length > 0) {
        return {
          ok: false,
          reason:
            'Reatribua as atividades abertas antes de desativar. O histórico é preservado, mas trabalho aberto não pode ficar órfão.',
          openActivities: open.length,
        }
      }

      const remote = await softDeleteMember(id)
      if (!remote.ok) {
        return { ok: false, reason: remote.reason, openActivities: 0 }
      }

      commit({ type: 'member/upsert', member: remote.data })
      audit('membro.desativado', id, 'acesso revogado, histórico preservado')
      return { ok: true }
    }

    return {
      audit,
      reloadProjects,
      reloadPeople,
      reloadCore,

      // --- C1 Pessoas (Supabase) ---

      async createMember(input) {
        const state = current()
        if (state.members.some((m) => m.userId === input.userId)) {
          return { error: 'Este usuário do SSO já tem ficha no Hub.' }
        }
        const managerId = input.managerId?.trim() || undefined
        const hierarchyCheck = validateManagerAssignment(
          state.members,
          '00000000-0000-0000-0000-000000000000',
          managerId,
        )
        if (!hierarchyCheck.ok) return { error: hierarchyCheck.reason }

        // Ficha e papel mínimo numa transação só (migration 022). A membership
        // do portal vem pelo trigger — a tela não precisa lembrar dela.
        const remote = await createMemberFromSso({
          userId: input.userId,
          ...(input.fullName?.trim() ? { fullName: input.fullName.trim() } : {}),
          ...(input.positionTitle?.trim() ? { positionTitle: input.positionTitle.trim() } : {}),
          ...(managerId ? { managerId } : {}),
          ...(input.githubLogin?.trim() ? { githubLogin: input.githubLogin.trim() } : {}),
          ...(input.weeklyCapacityHours !== undefined
            ? { weeklyCapacityHours: input.weeklyCapacityHours }
            : {}),
          ...(input.startedOn ? { startedOn: input.startedOn } : {}),
        })
        if (!remote.ok) return { error: remote.reason }

        const member = remote.data
        commit({
          type: 'member/add',
          member,
          roles: [{ memberId: member.id, role: 'membro', grantedAt: nowIso() }],
        })
        audit('membro.criado', member.fullName, member.email)
        return member
      },

      async linkMemberToSso(memberId, userId) {
        const remote = await linkMemberSso(memberId, userId)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'member/upsert', member: remote.data })
        audit('membro.vinculado_sso', remote.data.fullName, remote.data.email)
        return { ok: true }
      },

      async updateMember(id, patch) {
        if (patch.managerId !== undefined) {
          const nextManager = patch.managerId === null ? undefined : patch.managerId
          const hierarchyCheck = validateManagerAssignment(current().members, id, nextManager)
          if (!hierarchyCheck.ok) return hierarchyCheck
        }

        const remote = await patchMember(id, patch)
        if (!remote.ok) return { ok: false, reason: remote.reason }

        commit({ type: 'member/upsert', member: remote.data })
        audit('membro.alterado', id, Object.keys(patch).join(', '))
        return { ok: true }
      },

      async setMemberAvatar(memberId, file) {
        const member = current().members.find((m) => m.id === memberId)
        if (!member) return { ok: false, reason: 'Pessoa não encontrada.' }
        const result = file
          ? await uploadAvatar(memberId, file, member.avatarPath)
          : await removeAvatar(memberId, member.avatarPath)
        if (!result.ok) return result

        const { avatarPath: _old, ...rest } = member
        commit({
          type: 'member/upsert',
          member: result.path ? { ...rest, avatarPath: result.path } : rest,
        })
        audit('membro.foto', memberId, result.path ? 'trocada' : 'removida')
        return { ok: true }
      },

      deactivateMember,

      async reactivateMember(id) {
        const remote = await reactivateMemberRemote(id)
        if (!remote.ok) return remote
        commit({ type: 'member/upsert', member: remote.data })
        audit('membro.reativado', id, '')
        return { ok: true }
      },

      memberDeletionImpact(id) {
        const state = current()
        const finals = finalColumnIds()
        const activities = state.activities.filter((a) => a.assigneeId === id)
        return {
          activities: activities.length,
          openActivities: activities.filter((a) => !finals.has(a.columnId)).length,
          costs: state.costs.filter((c) => c.memberId === id).length,
          allocations: state.allocations.filter((a) => a.memberId === id).length,
          gold: state.gold.filter((g) => g.memberId === id).length,
          roles: state.memberRoles.filter((r) => r.memberId === id).length,
          messages: state.messages.filter((m) => m.authorId === id).length,
          managedMembers: state.members.filter((m) => m.managerId === id).length,
          ledProjects: state.projects.filter((p) => p.leaderId === id).length,
        }
      },

      async deleteMember(id) {
        // Q48 — banco não concede DELETE em member. Soft-delete.
        const member = current().members.find((m) => m.id === id)
        if (!member) return { ok: false, reason: 'Pessoa não encontrada.' }
        if (id === meRef.current.id) {
          return { ok: false, reason: 'Você não pode excluir a si mesmo.' }
        }

        const deactivated = await deactivateMember(id)
        if (!deactivated.ok) {
          return { ok: false, reason: deactivated.reason }
        }
        audit('membro.excluido', member.fullName, 'soft-delete / histórico preservado')
        return { ok: true }
      },

      async reassignActivities(fromMemberId, toMemberId) {
        const finals = finalColumnIds()
        // Só o trabalho em aberto muda de dono. Atividade concluída pertence a
        // quem a fez — reatribuir apagaria a autoria no histórico e na métrica.
        const affected = current().activities.filter(
          (a) => a.assigneeId === fromMemberId && !finals.has(a.columnId),
        )
        if (affected.length === 0) return { ok: true, count: 0 }

        const remote = await reassignActivitiesRemote(
          affected.map((a) => a.id),
          toMemberId,
          meRef.current.id,
        )
        if (!remote.ok) return { ok: false, reason: remote.reason }

        commit({ type: 'activities/upsert', activities: remote.data })
        audit('atividades.reatribuidas', fromMemberId, `${affected.length} → ${toMemberId}`)
        return { ok: true, count: affected.length }
      },

      async setMemberRoles(memberId, roles, reason) {
        const sensitive = roles.filter((r) => SENSITIVE_ROLES.includes(r))
        const existing = new Set(
          current()
            .memberRoles.filter((r) => r.memberId === memberId)
            .map((r) => r.role),
        )
        const newSensitive = sensitive.filter((r) => !existing.has(r))
        if (newSensitive.length > 0 && !reason?.trim()) {
          return {
            ok: false,
            reason: `Conceder ${newSensitive.join(' e ')} exige justificativa registrada.`,
          }
        }

        const remote = await replaceMemberRoles(memberId, roles, reason)
        if (!remote.ok) return { ok: false, reason: remote.reason }

        commit({ type: 'memberRoles/replace', memberId, roles: remote.data })
        audit('papeis.alterados', memberId, roles.join(', '))
        return { ok: true }
      },

      async setPersonCost(memberId, input, effectiveFrom) {
        // Corrigir a MESMA vigência é substituição; vigência nova é linha nova.
        // Por isso o delete antes: o banco tem unique (member_id, effective_from).
        await deletePersonCost(memberId, effectiveFrom)

        const remote = await insertPersonCost(
          {
            memberId,
            effectiveFrom,
            ...(input.salary !== undefined ? { salary: input.salary } : {}),
            ...(input.charges !== undefined ? { charges: input.charges } : {}),
            ...(input.benefits !== undefined ? { benefits: input.benefits } : {}),
          },
          meRef.current.id,
        )
        if (!remote.ok) return { ok: false, reason: remote.reason }

        commit({ type: 'cost/set', cost: remote.data })
        audit('custo.definido', memberId, `vigência ${effectiveFrom}`)
        return { ok: true }
      },

      async removePersonCost(memberId, effectiveFrom) {
        const remote = await deletePersonCost(memberId, effectiveFrom)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'cost/remove', memberId, effectiveFrom })
        audit('custo.removido', memberId, `vigência ${effectiveFrom}`)
        return { ok: true }
      },

      async setAllocation(memberId, projectId, month, percent) {
        const remote = await upsertAllocation({ memberId, projectId, month, percent })
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({
          type: 'allocation/set',
          memberId,
          projectId,
          month,
          allocation: remote.data ?? null,
        })
        return { ok: true }
      },

      // --- C2 Projetos e kanban (Supabase) ---

      async createProject(input) {
        const key =
          input.key?.toUpperCase() ??
          suggestProjectKey(
            input.name,
            current().projects.map((p) => p.key),
          )

        const remote = await insertProject(
          {
            key,
            name: input.name,
            type: input.type ?? 'sistema',
            visibility: input.visibility ?? 'departamento',
            color: input.color ?? 'media',
            ...(input.description?.trim() ? { description: input.description.trim() } : {}),
            ...(input.leaderId ? { leaderId: input.leaderId } : {}),
          },
          meRef.current.id,
        )
        if (!remote.ok) return { error: remote.reason }

        const { project, columns } = remote.data
        commit({
          type: 'project/add',
          project,
          columns,
          ...(input.repositoryId ? { repositoryId: input.repositoryId } : {}),
        })
        audit('projeto.criado', project.key, project.name)
        return project
      },

      async updateProject(id, patch) {
        const before = current().projects.find((p) => p.id === id)
        if (!before) return { ok: false, reason: 'Projeto não encontrado.' }

        const remote = await patchProject(id, patch)
        if (!remote.ok) return { ok: false, reason: remote.reason }

        commit({ type: 'project/upsert', project: remote.data })

        const previous = before as unknown as Record<string, unknown>
        const next = patch as Record<string, unknown>
        const changed = Object.keys(patch).filter((k) => previous[k] !== next[k])
        audit('projeto.editado', patch.key ?? before.key, changed.join(', ') || 'sem alteração')
        return { ok: true }
      },

      async linkProjectRepository(projectId, repositoryId) {
        if (!current().projects.some((p) => p.id === projectId)) {
          return { ok: false, reason: 'Projeto não encontrado.' }
        }
        const remote = await linkRepository(projectId, repositoryId)
        if (!remote.ok) return { ok: false, reason: remote.reason }

        commit({ type: 'project/repo', projectId, repositoryId })
        audit(
          'projeto.repo',
          projectId,
          repositoryId ? `vinculado a ${repositoryId}` : 'sem repositório',
        )
        return { ok: true }
      },

      async moveProject(projectId, toStageId, beforeId, afterId) {
        const state = current()
        const project = state.projects.find((p) => p.id === projectId)
        const stage = state.projectStages.find((st) => st.id === toStageId)
        if (!project || !stage) return { ok: false as const, reason: 'Projeto não encontrado.' }

        // D21 — a trava do rascunho. Validar antes de escrever evita que o
        // quadro mostre o card já movido e o banco diga que não.
        const hasRepository = state.repositories.some((r) => r.projectIds.includes(projectId))
        if (!canEnterStage(project, stage, hasRepository)) {
          return {
            ok: false as const,
            reason:
              'Complete o cadastro (nome, resumo, tipo, criticidade, responsável e repositório) antes de sair de Rascunhos.',
          }
        }

        const before = state.projects.find((p) => p.id === beforeId)?.boardPosition
        const after = state.projects.find((p) => p.id === afterId)?.boardPosition
        const boardPosition = positionBetween(before, after)

        // Otimista: arrastar precisa terminar no lugar em que se soltou, não
        // piscar de volta até a resposta chegar.
        commit({
          type: 'project/move',
          project: { ...project, stageId: toStageId, status: stage.status, boardPosition },
        })

        const remote = await moveProjectRemote(projectId, toStageId, boardPosition)
        if (!remote.ok) {
          commit({ type: 'project/move', project })
          return { ok: false as const, reason: remote.reason }
        }
        commit({ type: 'project/move', project: remote.data })

        if (project.stageId !== toStageId) {
          const from = state.projectStages.find((st) => st.id === project.stageId)?.name ?? '—'
          audit('projeto.etapa', project.key, `${from} → ${stage.name}`)
        }
        if (project.status !== remote.data.status) {
          audit('projeto.status', project.key, `${project.status} → ${remote.data.status}`)
        }
        return { ok: true as const }
      },

      async createColumn(input) {
        const name = input.name.trim()
        if (!name) return { ok: false, reason: 'Dê um nome à coluna.' }
        const columns = current()
          .columns.filter((c) => c.projectId === input.projectId)
          .sort((a, b) => a.position - b.position)
        // Coluna nova entra antes da de conclusão: é trabalho em andamento.
        const final = columns.find((c) => c.kind === 'final')
        const beforeFinal = final ? columns.filter((c) => c.position < final.position).at(-1) : columns.at(-1)
        const remote = await insertColumn({
          projectId: input.projectId,
          name,
          wipLimit: input.wipLimit,
          position: positionBetween(beforeFinal?.position, final?.position),
        })
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'column/upsert', column: remote.data })
        audit('quadro.coluna.criada', input.projectId, remote.data.name)
        return { ok: true }
      },

      async updateColumn(id, patch) {
        const column = current().columns.find((c) => c.id === id)
        if (!column) return { ok: false, reason: 'Coluna não encontrada.' }
        if (patch.name !== undefined && !patch.name.trim()) {
          return { ok: false, reason: 'Dê um nome à coluna.' }
        }
        const remote = await patchColumn(id, patch)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        // O papel no fluxo do PR é um por quadro: quem tinha perdeu.
        if (patch.flowRole) {
          for (const other of current().columns) {
            if (other.projectId === column.projectId && other.id !== id && other.flowRole === patch.flowRole) {
              commit({ type: 'column/upsert', column: { ...other, flowRole: null } })
            }
          }
        }
        commit({ type: 'column/upsert', column: remote.data })
        audit('quadro.coluna.alterada', column.projectId, `${column.name} → ${remote.data.name}`)
        return { ok: true }
      },

      async moveColumn(id, beforeId, afterId) {
        const columns = current().columns
        const column = columns.find((c) => c.id === id)
        if (!column) return { ok: false, reason: 'Coluna não encontrada.' }
        const before = columns.find((c) => c.id === beforeId)?.position
        const after = columns.find((c) => c.id === afterId)?.position
        const position = positionBetween(before, after)
        commit({ type: 'column/upsert', column: { ...column, position } })
        const remote = await patchColumn(id, { position })
        if (!remote.ok) {
          commit({ type: 'column/upsert', column })
          return { ok: false, reason: remote.reason }
        }
        commit({ type: 'column/upsert', column: remote.data })
        return { ok: true }
      },

      async deleteColumn(id, targetId) {
        const column = current().columns.find((c) => c.id === id)
        if (!column) return { ok: false, reason: 'Coluna não encontrada.' }
        const remote = await deleteColumnRemote(id, targetId)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'column/remove', id, targetId })
        audit('quadro.coluna.excluida', column.projectId, column.name)
        return { ok: true }
      },

      async createStage(input) {
        const name = input.name.trim()
        if (!name) return { ok: false, reason: 'Dê um nome à coluna.' }
        const stages = current().projectStages
        const last = stages[stages.length - 1]?.position
        const remote = await insertStage(
          { name, status: input.status, position: positionBetween(last, undefined) },
          meRef.current.id,
        )
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'stage/upsert', stage: remote.data })
        audit('projeto.coluna.criada', remote.data.name, remote.data.status)
        return { ok: true }
      },

      async updateStage(id, patch) {
        const stage = current().projectStages.find((st) => st.id === id)
        if (!stage) return { ok: false, reason: 'Coluna não encontrada.' }
        if (patch.name !== undefined && !patch.name.trim()) {
          return { ok: false, reason: 'Dê um nome à coluna.' }
        }
        const remote = await patchStage(id, patch)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'stage/upsert', stage: remote.data })
        if (patch.status !== undefined && patch.status !== stage.status) {
          audit('projeto.coluna.fase', stage.name, `${stage.status} → ${patch.status}`)
        }
        if (patch.name !== undefined && remote.data.name !== stage.name) {
          audit('projeto.coluna.renomeada', stage.name, remote.data.name)
        }
        return { ok: true }
      },

      async moveStage(id, beforeId, afterId) {
        const stages = current().projectStages
        const stage = stages.find((st) => st.id === id)
        if (!stage) return { ok: false, reason: 'Coluna não encontrada.' }
        const before = stages.find((st) => st.id === beforeId)?.position
        const after = stages.find((st) => st.id === afterId)?.position
        const position = positionBetween(before, after)

        commit({ type: 'stage/upsert', stage: { ...stage, position } })
        const remote = await patchStage(id, { position })
        if (!remote.ok) {
          commit({ type: 'stage/upsert', stage })
          return { ok: false, reason: remote.reason }
        }
        commit({ type: 'stage/upsert', stage: remote.data })
        return { ok: true }
      },

      async deleteStage(id, targetId) {
        const stage = current().projectStages.find((st) => st.id === id)
        if (!stage) return { ok: false, reason: 'Coluna não encontrada.' }
        const remote = await deleteStageRemote(id, targetId)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'stage/remove', id, targetId })
        audit('projeto.coluna.excluida', stage.name, targetId ?? '')
        return { ok: true }
      },

      async deleteProject(id) {
        const project = current().projects.find((p) => p.id === id)
        if (!project) return { ok: false, reason: 'Projeto não encontrado.' }

        const remote = await deleteProjectRemote(id)
        if (!remote.ok) return { ok: false, reason: remote.reason }

        commit({ type: 'project/remove', id })
        audit('projeto.excluido', project.key, project.name)
        return { ok: true }
      },

      createActivity,
      updateActivity,

      async moveActivity(activityId, toColumnId, beforeId, afterId) {
        const state = current()
        const activity = state.activities.find((a) => a.id === activityId)
        const column = state.columns.find((c) => c.id === toColumnId)
        if (!activity || !column) return { ok: false, reason: 'Atividade não encontrada.' }

        // Q50b vale na UI para dar resposta imediata; o trigger do banco é quem
        // realmente garante. Checar aqui evita um round-trip só para ouvir não.
        const verdict = canMoveActivity(activity, column)
        if (!verdict.ok) return verdict

        const before = state.activities.find((a) => a.id === beforeId)?.position
        const after = state.activities.find((a) => a.id === afterId)?.position
        const from = state.columns.find((c) => c.id === activity.columnId)
        const isFinal = column.kind === 'final'
        const wasFinal = from?.kind === 'final'
        const position = positionBetween(before, after)

        // Otimista: o card termina onde foi solto, sem piscar de volta até a
        // resposta. `completed_at`, evento e GOLD continuam vindo do banco.
        commit({ type: 'activity/upsert', activity: { ...activity, columnId: toColumnId, position } })

        // Movimento + evento numa transação (migration 011). O banco decide
        // `completed_at` e grava o evento só se a coluna mudou.
        const remote = await moveActivityRemote({
          activityId,
          columnId: toColumnId,
          position,
          labelIds: activity.labelIds,
        })
        if (!remote.ok) {
          commit({ type: 'activity/upsert', activity })
          return { ok: false, reason: remote.reason }
        }

        commit({ type: 'activity/upsert', activity: remote.data.activity })
        if (remote.data.event) commit({ type: 'event/push', event: remote.data.event })

        if (isFinal && !wasFinal && activity.assigneeId) {
          const onTime = !activity.dueOn || todayIso() <= activity.dueOn
          const credit = await insertGoldEntry(
            {
              memberId: activity.assigneeId,
              points: goldForActivity({
                difficulty: activity.difficulty,
                onTime,
                reviewed: false,
              }),
              reason: 'atividade_concluida',
              activityKey: activity.key,
              description: activity.title,
              month: currentMonth(),
            },
            meRef.current.id,
          )
          if (credit.ok) {
            commit({ type: 'gold/push', entry: credit.data })
          } else {
            // A atividade já está concluída; falhar o GOLD não desfaz a entrega.
            // Fica na auditoria para o ajuste manual encontrar depois.
            audit('gold.falhou', activity.key, credit.reason)
          }
        }

        // Q46 — reabertura estorna. O estorno é lançamento novo de sinal
        // oposto, nunca a remoção do crédito: o saldo é a soma das transações,
        // e apagar a original esconderia que a atividade foi reaberta.
        if (!isFinal && wasFinal && activity.assigneeId) {
          const original = state.gold.find(
            (g) => g.activityKey === activity.key && g.reason === 'atividade_concluida',
          )
          if (original) {
            const refund = await insertGoldEntry(
              {
                memberId: activity.assigneeId,
                points: -original.points,
                reason: 'estorno',
                activityKey: activity.key,
                description: `Reabertura de ${activity.key}`,
                month: currentMonth(),
              },
              meRef.current.id,
            )
            if (refund.ok) {
              commit({ type: 'gold/push', entry: refund.data })
            } else {
              audit('gold.estorno_falhou', activity.key, refund.reason)
            }
          }
        }
        return { ok: true }
      },

      async archiveActivity(id) {
        const activity = current().activities.find((a) => a.id === id)
        if (!activity) return { ok: false, reason: 'Atividade não encontrada.' }
        const remote = await archiveActivityRemote(id, activity.labelIds)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        // Sai do cache das vivas; o evento fica na timeline para quem restaurar.
        commit({ type: 'activity/remove', id })
        if (remote.data.event) commit({ type: 'event/push', event: remote.data.event })
        audit('atividade.arquivada', activity.key, activity.title)
        return { ok: true }
      },

      async restoreActivity(id) {
        const remote = await restoreActivityRemote(id, [])
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'activity/upsert', activity: remote.data.activity })
        if (remote.data.event) commit({ type: 'event/push', event: remote.data.event })
        audit('atividade.restaurada', remote.data.activity.key, remote.data.activity.title)
        return { ok: true }
      },

      async trashActivity(id) {
        const activity = current().activities.find((a) => a.id === id)
        if (!activity) return { ok: false, reason: 'Atividade não encontrada.' }
        const remote = await trashActivityRemote(id, activity.labelIds)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        // O banco passa a esconder a atividade e os eventos dela; o cache
        // acompanha. O evento de exclusão fica no banco, para a Auditoria.
        commit({ type: 'activity/remove', id })
        audit('atividade.excluida', activity.key, activity.title)
        return { ok: true }
      },

      async restoreActivityFromTrash(id) {
        const remote = await restoreActivityFromTrashRemote(id)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'activity/upsert', activity: remote.data.activity })
        if (remote.data.event) commit({ type: 'event/push', event: remote.data.event })
        audit('atividade.restaurada_da_lixeira', remote.data.activity.key, remote.data.activity.title)
        return { ok: true }
      },

      addChecklist(activityId, checklist) {
        return patchChecklists(activityId, (a) => [...a.checklists, checklist])
      },

      addChecklistItem(activityId, checklistId, text) {
        const trimmed = text.trim()
        if (!trimmed) return Promise.resolve({ ok: false, reason: 'Item vazio.' })
        return patchChecklists(activityId, (a) =>
          a.checklists.map((c) =>
            c.id !== checklistId
              ? c
              : { ...c, items: [...c.items, { id: uid(), text: trimmed, done: false }] },
          ),
        )
      },

      toggleChecklistItem(activityId, checklistId, itemId) {
        return patchChecklists(activityId, (a) => toggleChecklistItemIn(a, checklistId, itemId))
      },

      removeChecklist(activityId, checklistId) {
        return patchChecklists(activityId, (a) => a.checklists.filter((c) => c.id !== checklistId))
      },

      // --- C3 Chat ---

      async refreshProjectMessages(projectId) {
        const remote = await fetchProjectMessages(projectId)
        if (remote.ok) commit({ type: 'messages/project', projectId, messages: remote.data })
      },

      async sendMessage(projectId, body, attachmentName) {
        const remote = await insertProjectMessage(
          {
            projectId,
            body,
            ...(attachmentName ? { attachmentName } : {}),
          },
          meRef.current.id,
        )
        if (!remote.ok) {
          audit('chat.falhou', projectId, remote.reason)
          return { ok: false, reason: remote.reason }
        }
        commit({ type: 'message/push', message: remote.data })
        return { ok: true }
      },

      // --- C4 Chamados ---

      refreshTickets,

      // --- C10 Financeiro ---

      async saveBudget(month, amount, reason) {
        const remote = await upsertBudget(month, amount, meRef.current.id)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'budget/upsert', budget: remote.data })
        audit('budget.definido', month, reason ? `${amount} · motivo: ${reason}` : String(amount))
        return { ok: true }
      },

      async applyBudgetDefault(year, amount) {
        const remote = await applyBudgetDefaultRemote(year, amount)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        audit('budget.padrao', String(year), `${amount} · ${remote.data} mês(es)`)
        // A RPC escreve vários meses: relemos os do ano em vez de reproduzir
        // aqui a regra de quais ela tocou. Só o budget — recarregar o core
        // inteiro passaria a tela por "carregando" e a desmontaria.
        const fresh = await fetchBudgetMonths(year)
        if (fresh.ok) for (const budget of fresh.data) commit({ type: 'budget/upsert', budget })
        return { ok: true, applied: remote.data }
      },

      async addExpense(input) {
        // O valor em BRL é derivado aqui e gravado: o fechamento mensal precisa
        // do valor convertido na data do lançamento, não da cotação de hoje.
        const remote = await insertExpense(
          { ...input, amountBrl: normalizeToBrl(input) },
          meRef.current.id,
        )
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'expense/add', expense: remote.data })
        audit('despesa.criada', remote.data.vendor, String(remote.data.amountBrl))
        return { ok: true, id: remote.data.id }
      },

      async removeExpense(id) {
        const remote = await deleteExpense(id)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'expense/remove', id })
        audit('despesa.removida', id, '')
        return { ok: true }
      },

      async addExpenseInstallments(input, count) {
        const remote = await insertExpenseInstallments(input, count)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        for (const expense of remote.data) commit({ type: 'expense/add', expense })
        const total = remote.data.reduce((sum, e) => sum + e.amountBrl, 0)
        audit('despesa.parcelada', input.vendor, `${count}x · ${Math.round(total * 100) / 100}`)
        return { ok: true, ids: remote.data.map((e) => e.id), group: remote.data[0]?.installment?.group ?? '' }
      },

      async removeExpenseGroup(group) {
        const remote = await deleteExpenseGroup(group)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        for (const e of stateRef.current.expenses.filter((x) => x.installment?.group === group)) {
          commit({ type: 'expense/remove', id: e.id })
        }
        audit('despesa.parcelamento.removido', group, '')
        return { ok: true }
      },

      // --- C7 Monitoramento ---

      async errorToActivity(errorGroupId, projectId, columnId, assigneeId) {
        const group = current().errorGroups.find((g) => g.id === errorGroupId)
        if (!group) return { ok: false, reason: 'Grupo de erro não encontrado.' }
        const activity = await createActivity({
          projectId,
          columnId,
          title: group.title,
          description: `${group.occurrences} ocorrências · ${group.affectedUsers} usuários afetados`,
          assigneeId,
          priority: 'alta',
          difficulty: null,
          labelIds: [],
          origin: 'erro_producao',
        })
        if ('error' in activity) {
          audit('erro.conversao_falhou', group.title.slice(0, 40), activity.error)
          return { ok: false, reason: activity.error }
        }
        commit({
          type: 'errorGroup/patch',
          id: errorGroupId,
          patch: { status: 'reconhecido', activityId: activity.id },
        })
        audit('erro.convertido', group.title.slice(0, 40), activity.key)
        return { ok: true }
      },

      async acknowledgeAlert(alertId) {
        const remote = await acknowledgeAlertRemote(alertId, meRef.current.id)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'alert/patch', id: alertId, patch: { acknowledged: true } })
        audit('alerta.reconhecido', alertId, '')
        return { ok: true }
      },

      // --- C5 Rotinas ---

      async createRoutine(input) {
        const remote = await insertRoutine(input)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'routine/upsert', routine: remote.data })
        audit('rotina.criada', remote.data.name, recurrenceLabel(remote.data))
        return { ok: true }
      },

      async updateRoutine(id, input) {
        const remote = await patchRoutine(id, input)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'routine/upsert', routine: remote.data })
        audit('rotina.alterada', remote.data.name, recurrenceLabel(remote.data))
        return { ok: true }
      },

      async setRoutineActive(id, isActive) {
        const remote = await setRoutineActiveRemote(id, isActive)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'routine/upsert', routine: remote.data })
        audit(
          isActive ? 'rotina.reativada' : 'rotina.desativada',
          remote.data.name,
          isActive ? '' : 'ocorrências realizadas preservadas',
        )
        return { ok: true }
      },

      async deleteRoutine(id) {
        const state = current()
        const routine = state.routines.find((r) => r.id === id)
        const occurrences = state.occurrences.filter((o) => o.routineId === id).length
        // A FK `restrict` é quem garante (migration 019); esta checagem existe
        // só para a recusa chegar como frase, e não como erro de constraint.
        if (occurrences > 0) {
          return {
            ok: false,
            reason: `"${routine?.name ?? 'Esta rotina'}" tem ${occurrences} ocorrência(s) registrada(s) e o histórico não pode ser apagado. Desative em vez de excluir.`,
          }
        }

        const remote = await deleteRoutineRemote(id)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'routine/remove', id })
        audit('rotina.excluida', routine?.name ?? id, 'sem ocorrências registradas')
        return { ok: true }
      },

      async createCategory(input) {
        const remote = await insertCategory(input)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'category/upsert', category: remote.data })
        audit('categoria.criada', remote.data.name, remote.data.color)
        return { ok: true }
      },

      async updateCategory(id, patch) {
        const remote = await patchCategory(id, patch)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'category/upsert', category: remote.data })
        audit(
          patch.isActive === false ? 'categoria.desativada' : 'categoria.alterada',
          remote.data.name,
          Object.keys(patch).join(', '),
        )
        return { ok: true }
      },

      async createEvent(input) {
        const remote = await insertEvent(input, meRef.current.id)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'event/upsert', event: remote.data })
        audit('compromisso.criado', remote.data.title, `${remote.data.date} ${remote.data.time ?? 'dia inteiro'}`)
        return { ok: true }
      },

      async updateEvent(id, input) {
        const remote = await patchEvent(id, input)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'event/upsert', event: remote.data })
        audit('compromisso.alterado', remote.data.title, remote.data.date)
        return { ok: true }
      },

      async rescheduleEvent(id, date, time) {
        const existing = current().events.find((e) => e.id === id)
        if (!existing) return { ok: false, reason: 'Compromisso não encontrado.' }

        const remote = await rescheduleEventRemote(id, date, time)
        if (!remote.ok) return { ok: false, reason: remote.reason }

        commit({
          type: 'event/upsert',
          event: { ...existing, date, ...(time ? { time } : {}) },
        })
        audit('compromisso.remarcado', existing.title, `${existing.date} → ${date}`)
        return { ok: true }
      },

      async deleteEvent(id) {
        const existing = current().events.find((e) => e.id === id)
        const remote = await deleteEventRemote(id)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'event/remove', id })
        audit('compromisso.excluido', existing?.title ?? id, '')
        return { ok: true }
      },

      async rescheduleOccurrence(input) {
        const routine = current().routines.find((r) => r.id === input.routineId)
        const remote = await upsertOccurrenceSlot(input)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'occurrence/push', occurrence: remote.data })
        audit(
          input.status === 'cancelada' ? 'rotina.ocorrencia.cancelada' : 'rotina.ocorrencia.remarcada',
          routine?.name ?? input.routineId,
          input.status === 'cancelada'
            ? input.originalDate
            : `${input.originalDate} → ${input.date}`,
        )
        return { ok: true }
      },

      async registerOccurrence(input) {
        const remote = await insertOccurrence(input)
        if (!remote.ok) return { ok: false, reason: remote.reason }
        commit({ type: 'occurrence/push', occurrence: remote.data })
        return { ok: true }
      },

      resetAll() {
        commit({ type: 'reset' })
      },
    }
  }, [reloadProjects, reloadPeople, reloadCore, commit])

  // Tempo real: o que outra pessoa mudou aparece sem F5. Com escrita minha em
  // voo, espera ela terminar — senão a leitura traria o card de volta no meio
  // do arrasto.
  useLiveRefresh(PROJECT_LIVE_TABLES, async () => {
    if (writeQueues.current.size > 0) return false
    await reloadProjects({ silent: true })
  })
  useLiveRefresh(TICKET_LIVE_TABLES, () => actions.refreshTickets())

  const snapshot = React.useMemo<CoreSnapshot>(() => ({ state, me, status }), [state, me, status])

  return (
    <CoreActionsContext.Provider value={actions}>
      <CoreSnapshotContext.Provider value={snapshot}>{children}</CoreSnapshotContext.Provider>
    </CoreActionsContext.Provider>
  )
}

/** Só as ações. Identidade estável: não re-renderiza quando o estado muda. */
export function useCoreActions(): CoreActions {
  const ctx = React.useContext(CoreActionsContext)
  if (!ctx) throw new Error('useCoreActions precisa estar dentro de <CoreProvider>')
  return ctx
}

/** Estado + ações. Re-renderiza a cada mudança de estado — use quando a tela lê o estado. */
export function useCore(): CoreApi {
  const actions = useCoreActions()
  const snapshot = React.useContext(CoreSnapshotContext)
  if (!snapshot) throw new Error('useCore precisa estar dentro de <CoreProvider>')
  return React.useMemo<CoreApi>(
    () => ({
      ...actions,
      state: snapshot.state,
      me: snapshot.me,
      status: snapshot.status,
      projectsLoading: snapshot.status.projects.kind === 'carregando',
      projectsError:
        snapshot.status.projects.kind === 'erro' ? snapshot.status.projects.reason : null,
    }),
    [actions, snapshot],
  )
}
